import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
} from "axios";
import { useAppStore } from "../store/store";

export type HttpResult<T> =
  | { ok: true; data: T }
  | { ok: false; status?: number; message?: string };

let accessToken: string | null = null;

export const setAuthToken = (token: string | null) => {
  accessToken = token;
};

/**
 * URL'deki alan adına (hostname) göre Test / Prod API adresini otomatik algılar:
 * - https://admin-test.fixeraviation.com/ veya localhost -> https://api-test.fixeraviation.com/api/
 * - https://admin.fixeraviation.com/ -> https://api.fixeraviation.com/api/
 */
export const getApiBaseUrl = (): string => {
  if (typeof window !== "undefined") {
    const hostname = window.location.hostname.toLowerCase();

    // 1. TEST ORTAMI: admin-test.fixeraviation.com veya yerel geliştirme (localhost / 127.0.0.1)
    if (
      hostname === "admin-test.fixeraviation.com" ||
      hostname.includes("admin-test") ||
      hostname.includes("localhost") ||
      hostname === "127.0.0.1"
    ) {
      return "https://api-test.fixeraviation.com/api/";
    }

    // 2. PROD ORTAMI: admin.fixeraviation.com
    if (
      hostname === "admin.fixeraviation.com" ||
      (hostname.startsWith("admin.") && !hostname.includes("test"))
    ) {
      return "https://api.fixeraviation.com/api/";
    }
  }

  // Fallback: Ortam değişkeni veya güvenli varsayılan olarak test API
  return import.meta.env.VITE_API_BASE_URL || "https://api-test.fixeraviation.com/api/";
};

const api: AxiosInstance = axios.create({
  baseURL: getApiBaseUrl(),
  timeout: 120000,
  headers: {
    "Content-Type": "application/json",
  },
});

api.interceptors.request.use(
  (config) => {
    config.baseURL = getApiBaseUrl();
    if (accessToken) {
      config.headers = {
        ...config.headers,
        Authorization: `Bearer ${accessToken}`,
      };
    }
    return config;
  },
  (error) => Promise.reject(error),
);

api.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      useAppStore.getState().authActions.logout();
    }
    return Promise.reject(error);
  },
);

const mapError = (e: unknown): HttpResult<any> => {
  const err = e as AxiosError<any>;
  return {
    ok: false,
    status: err.response?.status,
    message:
      err.response?.data?.message ||
      err.response?.statusText ||
      err.message ||
      "Network error",
  };
};

export const httpGet = async <T>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<HttpResult<T>> => {
  try {
    const res = await api.get<T>(url, config);
    return { ok: true, data: res.data };
  } catch (e) {
    return mapError(e);
  }
};

export const httpPost = async <T>(
  url: string,
  body?: any,
  config?: AxiosRequestConfig,
): Promise<HttpResult<T>> => {
  try {
    const res = await api.post<T>(url, body, config);
    return { ok: true, data: res.data };
  } catch (e) {
    return mapError(e);
  }
};

export const httpPut = async <T>(
  url: string,
  body?: any,
  config?: AxiosRequestConfig,
): Promise<HttpResult<T>> => {
  try {
    const res = await api.put<T>(url, body, config);
    return { ok: true, data: res.data };
  } catch (e) {
    return mapError(e);
  }
};

export const httpDelete = async <T>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<HttpResult<T>> => {
  try {
    const res = await api.delete<T>(url, config);
    return { ok: true, data: res.data };
  } catch (e) {
    return mapError(e);
  }
};

export const httpPatch = async <T>(
  url: string,
  body?: any,
  config?: AxiosRequestConfig,
): Promise<HttpResult<T>> => {
  try {
    const res = await api.patch<T>(url, body, config);
    return { ok: true, data: res.data };
  } catch (e) {
    return mapError(e);
  }
};

export { api };
